import React from "react"
import { Form, Formik } from "formik"

import {
  ConnectorDialog,
  ConnectFormField,
} from "~/ui-rtk/components/features/connectors"
import { FormValues, useConnect } from "./connect"
import { toast } from "react-toastify"
import { isBadRequestError } from "~/ui-rtk/utils/http-utils"

import type { CustomerConnectorDialogFormPropsWithoutUri } from "../types"
import { connectorIcons } from "~/ui-rtk/shared/constants/image-paths.contants"
import { Button } from "~/ui-rtk/components/ui/controls"

type GoogleAdsManagementFormProps = CustomerConnectorDialogFormPropsWithoutUri

/**
 * Google Ads Management connect flow (agency/MCC model — no OAuth). The client
 * accepts our Manager-account link inside Google Ads, then enters their
 * 10-digit customer ID here. The backend verifies the account is actually
 * linked under our MCC before storing the mapping, so a typo'd or not-yet-
 * linked ID is rejected with a specific message.
 */
const GoogleAdsManagementForm: React.FC<GoogleAdsManagementFormProps> = ({
  companyConnectorId,
  isOpen,
  onClose,
  ...props
}) => {
  const { initialValues, validationSchema, isLoading, authenticate } =
    useConnect()

  const handleConnect = async (values: FormValues) => {
    if (isLoading) {
      return
    }

    if (!companyConnectorId) {
      throw new Error("Missing company connector id")
    }

    try {
      await authenticate({
        companyConnectorId,
        authCode: values.customerId,
        ...props,
      })
      onClose()
    } catch (err) {
      // The backend returns a specific 400 for "not linked to our Manager
      // account yet" and for malformed IDs — surface its message verbatim.
      if (isBadRequestError(err)) {
        const message =
          (err as { data?: { message?: string } })?.data?.message ??
          "Invalid Google Ads Customer ID"
        toast.error(message, {
          toastId: "google-ads-management-connect-error",
        })
        return
      }

      toast.error("Unexpected error. Please, try again later.", {
        toastId: "google-ads-management-connect-unexpected-error",
      })
    }
  }

  return (
    <ConnectorDialog
      imgSrc={connectorIcons.googleAds}
      alt="Google Ads Logo"
      title="Connect Google Ads"
      isOpen={isOpen}
      onClose={onClose}
    >
      <p className="leading-5 text-left text-base text-platinum">
        Before connecting, accept the Manager account link request from Marathon
        Data in Google Ads (Admin &rarr; Account access &rarr; Managers), then
        enter your 10-digit Google Ads Customer ID (shown in the top-right
        corner of Google Ads).
      </p>
      <Formik
        onSubmit={handleConnect}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
      >
        {({ touched, errors }) => (
          <Form className="flex flex-col w-full gap-4 mt-8">
            <ConnectFormField
              name="customerId"
              label="Google Ads Customer ID*"
              placeholder="123-456-7890"
              isError={Boolean(touched.customerId && errors.customerId)}
            />

            <Button
              variant={{ variant: "solid", color: "white" }}
              type="submit"
              isLoading={isLoading}
            >
              Connect
            </Button>
          </Form>
        )}
      </Formik>
    </ConnectorDialog>
  )
}

export default GoogleAdsManagementForm
